import { useFormik } from "formik";
import ParticlesAuth from "pages/AuthenticationInner/ParticlesAuth";
import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createSelector } from "reselect";
import * as Yup from "yup";
import { loginUser, resetLoginFlag } from "../../slices/thunk";
import { fetchLatestPermissionsLogin } from "slices/auth/login/thunk";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const Signin = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [passwordShow, setPasswordShow] = useState(false);

  const selectAccountAndLogin = createSelector(
    (state: any) => state.Account,
    (state: any) => state.Login,
    (account, login) => ({
      user: account.user,
      error: login.error,
      loading: login.loading,
      errorMsg: login.errorMsg,
    })
  );

  const { user, error, errorMsg } = useSelector(selectAccountAndLogin);

  const [initialValues, setInitialValues] = useState({
    mobile_number: "",
    password: "",
    remember_me: false,
  });

  useEffect(() => {
    if (user) {
      const updatedUserData =
        process.env.REACT_APP_DEFAULTAUTH === "firebase"
          ? user.multiFactor.user.mobile_number
          : user.mobile_number;
      const updatedUserPassword =
        process.env.REACT_APP_DEFAULTAUTH === "firebase" ? "" : user.password;

      setInitialValues({
        mobile_number: updatedUserData || "",
        password: updatedUserPassword,
        remember_me: false,
      });
    }
  }, [user]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      mobile_number: Yup.string()
        .required("Please Enter Your Mobile Number")
        .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
      password: Yup.string().required("Please Enter Your Password"),
      remember_me: Yup.boolean().default(false),
    }),
    onSubmit: async (values) => {
      try {
        const loginPayload = {
          mobile_number: values.mobile_number,
          password: values.password,
          remember_me: values.remember_me || false,
        };
        const data = await dispatch(loginUser(loginPayload, navigate));
        console.log("Login data : ", data?.data?.user_id);

        const result = await dispatch(
          fetchLatestPermissionsLogin(data?.data?.user_id)
        ).unwrap();

        if (!result) {
          console.error("Failed to fetch latest permissions");
        }

        toast.success(data?.message || "User logged in successfully", {
          toastId: "loginSuccess",
        });
        navigate("/dashboard");
      } catch (err: any) {
        const errorMessage =
          typeof err === "string"
            ? err
            : err?.error ||
              err?.message ||
              "Login failed. Please check credentials.";
        toast.error(errorMessage, { toastId: "loginError" });
      }
    },
  });

  useEffect(() => {
    if (errorMsg) {
      setTimeout(() => {
        dispatch(resetLoginFlag());
      }, 3000);
    }
  }, [dispatch, errorMsg]);

  document.title = "Login | Inventory And Asset Management";

  return (
    <ParticlesAuth>
      <Col xs={12} xxl={12} className="d-flex justify-content-center">
        <div style={{ maxWidth: 500, width: "100%" }}>
          <Card className="mb-0 border-0 shadow-none">
            <Card.Body className="p-sm-5 m-lg-4">
              {/* Header Section */}
              <div className="text-center">
                <h5 className="fs-3xl">Welcome Back</h5>
                <p className="text-muted">Sign in to continue to Dashboard</p>
              </div>

              {/* Form Section */}
              <div className="p-2 mt-5">
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                  }}
                  noValidate
                >
                  {/* Mobile Number Field */}
                  <Form.Group className="mb-3" controlId="formMobileNumber">
                    <Form.Label>
                      Mobile Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="mobile_number"
                      placeholder="Enter your mobile number"
                      required
                      maxLength={10}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.mobile_number || ""}
                      isInvalid={
                        validation.touched.mobile_number &&
                        !!validation.errors.mobile_number
                      }
                      autoComplete="tel"
                    />
                    <Form.Control.Feedback type="invalid">
                      {validation.errors.mobile_number}
                    </Form.Control.Feedback>
                  </Form.Group>

                  {/* Password Field */}
                  <Form.Group className="mb-3" controlId="formPassword">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label className="mb-0">
                        Password <span className="text-danger">*</span>
                      </Form.Label>
                      <Link
                        to={`${process.env.PUBLIC_URL}/forgot-password`}
                        className="text-muted text-decoration-none"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Forgot password?
                      </Link>
                    </div>
                    <div className="position-relative auth-pass-inputgroup">
                      <Form.Control
                        type={passwordShow ? "text" : "password"}
                        placeholder="Enter your password"
                        required
                        name="password"
                        value={validation.values.password || ""}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        isInvalid={
                          validation.touched.password &&
                          !!validation.errors.password
                        }
                        autoComplete="current-password"
                        style={{ paddingRight: "45px" }}
                      />
                      <Button
                        variant="link"
                        className="position-absolute end-0 text-decoration-none text-muted"
                        type="button"
                        onClick={() => setPasswordShow((prev) => !prev)}
                        style={{
                          zIndex: 2,
                          width: 35,
                          height: "38px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: 15,
                          marginRight: 5,
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginTop:
                            validation.touched.password &&
                            !!validation.errors.password
                              ? "-10px"
                              : 0,
                        }}
                        aria-label={
                          passwordShow ? "Hide password" : "Show password"
                        }
                      >
                        {passwordShow ? (
                          <Visibility style={{ fontSize: 20 }} />
                        ) : (
                          <VisibilityOff style={{ fontSize: 20 }} />
                        )}
                      </Button>
                      <Form.Control.Feedback type="invalid">
                        {validation.errors.password}
                      </Form.Control.Feedback>
                    </div>
                  </Form.Group>

                  {/* Remember Me Checkbox */}
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="rememberMe"
                      name="remember_me"
                      label="Remember me"
                      checked={validation.values.remember_me || false}
                      onChange={(e) => {
                        validation.setFieldValue(
                          "remember_me",
                          e.target.checked
                        );
                      }}
                      style={{ fontSize: "0.875rem" }}
                    />
                  </Form.Group>

                  {/* Error Message Display */}
                  {error && errorMsg && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}

                  {/* Submit Button */}
                  <div className="mt-4">
                    <Button
                      className="btn btn-primary w-100"
                      type="submit"
                      disabled={validation.isSubmitting}
                      style={{ minHeight: "42px" }}
                    >
                      {validation.isSubmitting ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Signing in...
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Col>
    </ParticlesAuth>
  );
};

export default Signin;
